import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { trackEvent } from "../analytics";
import {
  createImprovementIdea,
  fetchImprovementIdeas,
  setImprovementImplemented,
  toggleImprovementVote,
} from "../api/improvementIdeas";
import { ANALYTICS } from "../lib/analyticsEvents";
import { userMayUseActivitiesImportPage } from "../lib/adminImport";

function formatDate(isoString) {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString("de-CH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function VerbesserungenPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [sort, setSort] = useState("top");
  const [showImplemented, setShowImplemented] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [voteBusyId, setVoteBusyId] = useState(null);
  const [implementedBusyId, setImplementedBusyId] = useState(null);
  const [shippedNotes, setShippedNotes] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const mayManageIdeas = userMayUseActivitiesImportPage(user);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchImprovementIdeas({ sort });
      setIdeas(data.ideas || []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, [user, sort]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    load();
  }, [authLoading, user, load]);

  const visibleIdeas = ideas.filter((idea) =>
    showImplemented ? idea.implemented : !idea.implemented
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await createImprovementIdea({ title, body });
      trackEvent(ANALYTICS.improvement_idea_submitted, { source: "verbesserungen" });
      setTitle("");
      setBody("");
      setComposerOpen(false);
      setShowImplemented(false);
      await load();
    } catch (err) {
      setSubmitError(err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (idea) => {
    if (!user || idea.implemented || voteBusyId) return;
    const previous = ideas;
    const nextVoted = !idea.voted_by_me;
    setIdeas((current) =>
      current.map((item) =>
        item.id === idea.id
          ? {
              ...item,
              voted_by_me: nextVoted,
              vote_count: Math.max(0, item.vote_count + (nextVoted ? 1 : -1)),
            }
          : item
      )
    );
    setVoteBusyId(idea.id);
    setError(null);
    try {
      const result = await toggleImprovementVote(idea.id);
      setIdeas((current) =>
        current.map((item) =>
          item.id === idea.id
            ? {
                ...item,
                voted_by_me: Boolean(result?.voted),
                vote_count:
                  typeof result?.vote_count === "number"
                    ? result.vote_count
                    : item.vote_count,
              }
            : item
        )
      );
    } catch (err) {
      setIdeas(previous);
      setError(err.message || "Abstimmung fehlgeschlagen.");
    } finally {
      setVoteBusyId(null);
    }
  };

  const handleImplementedToggle = async (idea) => {
    if (!mayManageIdeas || implementedBusyId) return;
    setImplementedBusyId(idea.id);
    setError(null);
    const nextImplemented = !idea.implemented;
    const previous = ideas;
    const note = (shippedNotes[idea.id] || "").trim();
    setIdeas((current) =>
      current.map((item) =>
        item.id === idea.id
          ? {
              ...item,
              implemented: nextImplemented,
              shipped_note: nextImplemented ? note || null : null,
            }
          : item
      )
    );
    try {
      const result = await setImprovementImplemented(idea.id, nextImplemented, {
        shippedNote: nextImplemented ? note : undefined,
      });
      setIdeas((current) =>
        current.map((item) =>
          item.id === idea.id
            ? {
                ...item,
                implemented: Boolean(result?.implemented),
                shipped_note: result?.shipped_note ?? null,
              }
            : item
        )
      );
      setExpandedId(null);
    } catch (err) {
      setIdeas(previous);
      setError(err.message || "Status konnte nicht gespeichert werden.");
    } finally {
      setImplementedBusyId(null);
    }
  };

  const openView = (nextSort) => {
    setShowImplemented(false);
    setSort(nextSort);
    setExpandedId(null);
  };

  if (authLoading) {
    return (
      <div className="verbesserungen-page">
        <div className="verbesserungen-loading">Laden…</div>
      </div>
    );
  }

  if (!user) {
    const redirect = `/verbesserungen`;
    return (
      <div className="verbesserungen-page">
        <header className="verbesserungen-header">
          <Link to="/mehr" className="verbesserungen-back" aria-label="Zurück">
            ← Zurück
          </Link>
          <h1 className="verbesserungen-title">Mitgestalten</h1>
        </header>
        <p className="verbesserungen-hinweis">
          Melde dich an, um Verbesserungsvorschläge zu sehen, abzustimmen und
          eigene einzubringen.
        </p>
        <button
          type="button"
          className="verbesserungen-login-btn"
          onClick={() =>
            navigate(`/login?redirect=${encodeURIComponent(redirect)}`)
          }
        >
          Anmelden
        </button>
      </div>
    );
  }

  return (
    <div className="verbesserungen-page">
      <header className="verbesserungen-header">
        <Link to="/mehr" className="verbesserungen-back" aria-label="Zurück">
          ← Zurück
        </Link>
        <h1 className="verbesserungen-title">Mitgestalten</h1>
      </header>

      {composerOpen ? (
        <form className="verbesserungen-form" onSubmit={handleSubmit}>
          <label className="verbesserungen-label" htmlFor="idea-title">
            Neuer Vorschlag
          </label>
          <input
            id="idea-title"
            className="verbesserungen-input"
            type="text"
            maxLength={200}
            placeholder="Kurzer Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
            autoFocus
          />
          <textarea
            id="idea-body"
            className="verbesserungen-textarea"
            maxLength={500}
            rows={3}
            placeholder="Optional: etwas Kontext (max. 500 Zeichen)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {submitError && (
            <div className="verbesserungen-form-error" role="alert">
              {submitError}
            </div>
          )}
          <div className="verbesserungen-form-actions">
            <button
              type="submit"
              className="verbesserungen-submit"
              disabled={submitting || !title.trim()}
            >
              {submitting ? "Senden…" : "Vorschlag senden"}
            </button>
            <button
              type="button"
              className="verbesserungen-cancel"
              onClick={() => {
                setComposerOpen(false);
                setSubmitError(null);
              }}
              disabled={submitting}
            >
              Abbrechen
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="verbesserungen-compose-btn"
          onClick={() => setComposerOpen(true)}
        >
          Vorschlag schreiben
        </button>
      )}

      <div className="verbesserungen-sort" role="group" aria-label="Ansicht">
        <button
          type="button"
          className={`verbesserungen-sort-btn ${
            !showImplemented && sort === "top" ? "active" : ""
          }`}
          aria-pressed={!showImplemented && sort === "top"}
          onClick={() => openView("top")}
        >
          Top
        </button>
        <button
          type="button"
          className={`verbesserungen-sort-btn ${
            !showImplemented && sort === "new" ? "active" : ""
          }`}
          aria-pressed={!showImplemented && sort === "new"}
          onClick={() => openView("new")}
        >
          Neu
        </button>
        <button
          type="button"
          className={`verbesserungen-sort-btn ${showImplemented ? "active" : ""}`}
          aria-pressed={showImplemented}
          onClick={() => {
            setShowImplemented(true);
            setExpandedId(null);
          }}
        >
          Umgesetzt
        </button>
      </div>

      {loading && <div className="verbesserungen-loading">Laden…</div>}
      {error && (
        <div className="verbesserungen-error" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && visibleIdeas.length === 0 && (
        <p className="verbesserungen-empty">
          {showImplemented
            ? "Noch nichts umgesetzt."
            : "Noch keine offenen Vorschläge."}
        </p>
      )}

      {!loading && visibleIdeas.length > 0 && (
        <ul className="verbesserungen-list">
          {visibleIdeas.map((idea) => {
            const expanded = expandedId === idea.id;
            const canExpand = Boolean(idea.body) || mayManageIdeas;
            return (
              <li key={idea.id} className="verbesserungen-row">
                <button
                  type="button"
                  className={`verbesserungen-vote ${idea.voted_by_me ? "voted" : ""} ${
                    idea.implemented ? "locked" : ""
                  }`}
                  onClick={() => handleVote(idea)}
                  disabled={voteBusyId === idea.id || idea.implemented}
                  aria-pressed={idea.voted_by_me}
                  aria-label={
                    idea.implemented
                      ? "Abstimmung geschlossen"
                      : idea.voted_by_me
                      ? "Stimme zurücknehmen"
                      : "Dafür stimmen"
                  }
                >
                  <span className="verbesserungen-vote-icon" aria-hidden>
                    {idea.voted_by_me ? "▲" : "△"}
                  </span>
                  <span className="verbesserungen-vote-count">{idea.vote_count}</span>
                </button>
                <div className="verbesserungen-row-main">
                  <h2 className="verbesserungen-row-title">
                    {canExpand ? (
                      <button
                        type="button"
                        className="verbesserungen-row-toggle"
                        aria-expanded={expanded}
                        onClick={() =>
                          setExpandedId((current) =>
                            current === idea.id ? null : idea.id
                          )
                        }
                      >
                        {idea.title}
                      </button>
                    ) : (
                      idea.title
                    )}
                  </h2>
                  <div className="verbesserungen-row-meta">
                    <time dateTime={idea.created_at || undefined}>
                      {formatDate(idea.created_at)}
                    </time>
                    {idea.is_mine ? (
                      <span className="verbesserungen-mine-badge">Dein Vorschlag</span>
                    ) : null}
                  </div>
                  {showImplemented && idea.shipped_note ? (
                    <p className="verbesserungen-shipped-note">{idea.shipped_note}</p>
                  ) : null}
                  {expanded && idea.body ? (
                    <p className="verbesserungen-row-body">{idea.body}</p>
                  ) : null}
                  {expanded && mayManageIdeas ? (
                    <div className="verbesserungen-row-admin">
                      {!idea.implemented ? (
                        <textarea
                          className="verbesserungen-shipped-note-input"
                          maxLength={200}
                          rows={2}
                          placeholder="Optional: Kurznotiz für die Person (max. 200 Zeichen)"
                          value={shippedNotes[idea.id] || ""}
                          onChange={(e) =>
                            setShippedNotes((prev) => ({
                              ...prev,
                              [idea.id]: e.target.value,
                            }))
                          }
                        />
                      ) : null}
                      <button
                        type="button"
                        className={`verbesserungen-implemented-toggle ${
                          idea.implemented ? "active" : ""
                        }`}
                        onClick={() => handleImplementedToggle(idea)}
                        disabled={implementedBusyId !== null}
                        aria-pressed={idea.implemented}
                      >
                        {idea.implemented
                          ? "Als nicht umgesetzt markieren"
                          : "Als umgesetzt markieren"}
                      </button>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
